import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, CaretLeft, CaretRight, Plus, X,
  Buildings, VideoCamera, Gavel, Question, Gear, Warning, Clock, UsersThree, CaretDown, Check, EnvelopeSimple,
  Phone, MapPin, ArrowSquareOut, Eye,
} from "@phosphor-icons/react";
import ExpedienteQuickView from "@/components/expediente/ExpedienteQuickView";

const TIPO_META = {
  visita: { label: "Visita", color: "blue", Icon: Buildings },
  videoperitaje: { label: "Videoperitaje", color: "teal", Icon: VideoCamera },
  juicio: { label: "Juicio", color: "amber", Icon: Gavel },
  otro: { label: "Otro", color: "slate", Icon: Question },
};

const ROLES_VEN_TODO = new Set(["admin", "gestor", "gestor_senior"]);

const fmtDate = (d) => {
  // YYYY-MM-DD en hora local (NO usar toISOString → desfasa en zonas != UTC)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fmtTime = (s) => {
  try { return new Date(s).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const startOfWeek = (d) => {
  const r = new Date(d);
  const day = (r.getDay() + 6) % 7; // lunes=0
  r.setDate(r.getDate() - day);
  r.setHours(0, 0, 0, 0);
  return r;
};
const startOfMonth = (d) => { const r = new Date(d.getFullYear(), d.getMonth(), 1); return r; };
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

export default function CalendarPage() {
  const { user } = useAuth();
  const canSeeAll = ROLES_VEN_TODO.has(user?.role);
  const navigate = useNavigate();
  // Vista inicial: respeta ?view=day|week|month en la URL. Si no la hay y
  // estamos en móvil, "day" por defecto (perito en ruta necesita ver hoy).
  const initialView = (() => {
    if (typeof window === "undefined") return "month";
    const fromUrl = new URLSearchParams(window.location.search).get("view");
    if (["day", "week", "month"].includes(fromUrl)) return fromUrl;
    return window.matchMedia("(max-width: 767px)").matches ? "day" : "month";
  })();
  const [view, setView] = useState(initialView);
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [peritos, setPeritos] = useState([]);
  const [filterPeritos, setFilterPeritos] = useState([]); // ids vacío = todos
  const [filterTipos, setFilterTipos] = useState([]); // vacío = todos
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [creating, setCreating] = useState(false);
  const [quickViewExpId, setQuickViewExpId] = useState(null);

  // Rango según vista
  const range = useMemo(() => {
    if (view === "month") {
      const start = startOfMonth(cursor);
      const end = endOfMonth(cursor);
      // ampliamos a la semana completa para que cubra el grid
      const gridStart = startOfWeek(start);
      const gridEnd = new Date(end); gridEnd.setDate(gridEnd.getDate() + (6 - ((end.getDay() + 6) % 7)));
      return { start: gridStart, end: gridEnd };
    }
    if (view === "week") {
      const start = startOfWeek(cursor);
      const end = new Date(start); end.setDate(end.getDate() + 6);
      return { start, end };
    }
    const start = new Date(cursor); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [cursor, view]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: fmtDate(range.start), date_to: fmtDate(range.end) });
      filterPeritos.forEach((id) => params.append("perito_id", id));
      filterTipos.forEach((t) => params.append("tipo", t));
      const r = await api.get(`/calendar/events?${params.toString()}`);
      setEvents(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando eventos");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (canSeeAll) {
      api.get("/peritos").then((r) => setPeritos(r.data || [])).catch(() => {});
    }
  }, [canSeeAll]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [cursor, view, filterPeritos, filterTipos]);

  const move = (delta) => {
    const r = new Date(cursor);
    if (view === "month") r.setMonth(r.getMonth() + delta);
    else if (view === "week") r.setDate(r.getDate() + delta * 7);
    else r.setDate(r.getDate() + delta);
    setCursor(r);
  };

  return (
    <div className="space-y-4 px-3 md:px-6 py-5 max-w-screen-2xl mx-auto" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-display text-[19px] font-semibold tracking-tight">Agenda</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/calendar/configuracion")}
            data-testid="cal-settings-btn"
            className="text-[13px] h-9 px-3 border border-border bg-white rounded-lg hover:bg-background inline-flex items-center gap-1.5 transition-colors"
          >
            <Gear className="w-4 h-4" weight="regular" /> Configuración
          </button>
          <button
            onClick={() => setCreating(true)}
            data-testid="cal-new-event-btn"
            className="text-[13px] h-9 px-3 bg-brand hover:bg-brand/90 text-white rounded-lg font-semibold inline-flex items-center gap-1.5 transition-colors"
          >
            <Plus className="w-4 h-4" weight="regular" /> Nueva cita
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCursor(new Date())}
            className="text-[13px] h-8 px-3 border border-border bg-white rounded-lg hover:bg-background transition-colors"
          >Hoy</button>
          <div className="flex items-center bg-white border border-border rounded-lg overflow-hidden">
            <button onClick={() => move(-1)} className="h-8 px-2 hover:bg-background transition-colors text-muted hover:text-foreground" title="Anterior"><CaretLeft className="w-4 h-4" weight="regular" /></button>
            <span className="w-px h-5 bg-border" />
            <button onClick={() => move(1)} className="h-8 px-2 hover:bg-background transition-colors text-muted hover:text-foreground" title="Siguiente"><CaretRight className="w-4 h-4" weight="regular" /></button>
          </div>
          <span className="text-[15px] font-semibold ml-2 text-foreground lowercase" data-testid="cal-current-period">
            {cursor.toLocaleDateString("es-ES", { month: "long", year: "numeric", ...(view !== "month" ? { day: "numeric" } : {}) })}
          </span>
        </div>
        <div className="inline-flex items-center bg-white border border-border rounded-lg overflow-hidden">
          {["month", "week", "day"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              data-testid={`cal-view-${v}`}
              className={`text-[12px] h-8 px-3 font-medium transition-colors border-r border-border last:border-r-0 ${
                view === v ? "bg-brand text-white font-semibold" : "text-muted hover:bg-background"
              }`}
            >
              {v === "month" ? "Mes" : v === "week" ? "Semana" : "Día"}
            </button>
          ))}
        </div>
      </div>

      {/* Filters — leyenda con puntos de color */}
      <div className="flex flex-wrap items-center gap-2 text-[13px]">
        <span className="text-muted text-[12px]">Tipos:</span>
        {Object.entries(TIPO_META).map(([k, m]) => {
          const active = filterTipos.includes(k);
          const DOT_COLOR = { blue: "#2563EB", teal: "#0F766E", amber: "#E5862C", slate: "#9CA3AF" }[m.color];
          return (
            <button
              key={k}
              onClick={() => setFilterTipos((p) => active ? p.filter((x) => x !== k) : [...p, k])}
              className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full border transition-colors ${
                active ? "bg-white border-border text-foreground" : "bg-background border-transparent text-muted hover:bg-white"
              }`}
              data-testid={`cal-filter-tipo-${k}`}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: DOT_COLOR }} />
              <span>{m.label}</span>
            </button>
          );
        })}
        {canSeeAll && peritos.length > 0 && (
          <PeritosFilter
            peritos={peritos}
            selected={filterPeritos}
            onChange={setFilterPeritos}
          />
        )}
      </div>

      {loading && <p className="text-xs text-muted">Cargando…</p>}

      {/* Calendario + panel lateral fijo */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3 items-start">
        <div className="min-w-0">
          {view === "month" && <MonthGrid cursor={cursor} events={events} peritos={peritos} canSeeAll={canSeeAll} onSelect={setSelectedEvent} />}
          {view === "week" && <WeekGrid cursor={cursor} events={events} peritos={peritos} canSeeAll={canSeeAll} onSelect={setSelectedEvent} />}
          {view === "day" && <DayList cursor={cursor} events={events} peritos={peritos} canSeeAll={canSeeAll} onSelect={setSelectedEvent} />}
        </div>
        <VisitasProgramadasPanel
          events={events}
          peritos={peritos}
          canSeeAll={canSeeAll}
          onSelect={setSelectedEvent}
        />
      </div>

      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onUpdated={() => { setSelectedEvent(null); load(); }}
          onOpenQuickView={(expId) => { setQuickViewExpId(expId); setSelectedEvent(null); }}
          canEdit={canSeeAll || selectedEvent.perito_id === user?.id}
        />
      )}
      <ExpedienteQuickView
        expedienteId={quickViewExpId}
        onClose={() => setQuickViewExpId(null)}
      />
      {creating && (
        <CreateEventModal
          peritos={canSeeAll ? peritos : []}
          defaultPeritoId={user?.id}
          canSeeAll={canSeeAll}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

// ===== Vistas =====
// ----- Panel lateral: Visitas programadas -----
function VisitasProgramadasPanel({ events, peritos, canSeeAll, onSelect }) {
  // Sólo citas de hoy en adelante, ordenadas crono. Agrupadas por día.
  const grouped = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const future = (events || [])
      .filter((e) => {
        const d = new Date(e.starts_at);
        return !isNaN(d) && d >= today;
      })
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
    const map = new Map();
    future.forEach((e) => {
      const k = (e.starts_at || "").slice(0, 10);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    });
    return Array.from(map.entries());  // [['YYYY-MM-DD', [events…]], …]
  }, [events]);

  const total = grouped.reduce((acc, [, items]) => acc + items.length, 0);

  return (
    <aside
      className="bg-white border border-border rounded-[10px] overflow-hidden lg:sticky lg:top-3 lg:max-h-[calc(100vh-90px)] flex flex-col"
      data-testid="visitas-programadas-panel"
    >
      <header className="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/70">
        <span className="w-7 h-7 rounded-md bg-brand/10 border border-brand/20 flex items-center justify-center">
          <CalendarIcon className="w-3.5 h-3.5 text-brand" weight="duotone" />
        </span>
        <h3 className="font-display font-bold text-[13px] flex-1">Visitas programadas</h3>
        <span className="text-[10.5px] font-bold text-muted">{total} cita{total === 1 ? "" : "s"}</span>
      </header>

      <div className="overflow-y-auto flex-1 px-3 py-3 space-y-4" data-testid="visitas-programadas-list">
        {grouped.length === 0 && (
          <div className="text-[12px] text-muted italic text-center py-6">
            No hay citas próximas.
          </div>
        )}
        {grouped.map(([dayKey, items]) => <DayGroup key={dayKey} dayKey={dayKey} items={items} peritos={peritos} canSeeAll={canSeeAll} onSelect={onSelect} />)}
      </div>
    </aside>
  );
}

function DayGroup({ dayKey, items, peritos, canSeeAll, onSelect }) {
  const date = new Date(`${dayKey}T00:00:00`);
  const dayNum = date.getDate();
  const wd = date.toLocaleDateString("es-ES", { weekday: "long" });
  const month = date.toLocaleDateString("es-ES", { month: "long" });
  return (
    <div data-testid={`visitas-day-${dayKey}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 rounded-md bg-foreground text-white text-[12px] font-bold flex items-center justify-center flex-shrink-0">
          {dayNum}
        </span>
        <span className="text-[11.5px] font-semibold text-foreground capitalize">
          {wd} <span className="text-muted">· {month}</span>
        </span>
      </div>
      <ul className="space-y-1.5 ml-1">
        {items.map((ev) => <VisitaProgramadaItem key={ev.id} event={ev} peritos={peritos} canSeeAll={canSeeAll} onSelect={onSelect} />)}
      </ul>
    </div>
  );
}

function VisitaProgramadaItem({ event, peritos, canSeeAll, onSelect }) {
  const meta = TIPO_META[event.tipo] || TIPO_META.otro;
  const peri = canSeeAll ? peritos.find((p) => p.id === event.perito_id) : null;
  const barColor = {
    blue: "bg-[#2563EB]",
    teal: "bg-[#0F766E]",
    amber: "bg-[#D97706]",
    slate: "bg-[#94A3B8]",
  }[meta.color];
  const numSiniestro = event.expediente_id_human || event.expediente_numero || event.expediente_id;
  return (
    <li
      onClick={() => onSelect?.(event)}
      data-testid={`visita-programada-${event.id}`}
      className="relative pl-3 pr-2 py-1.5 rounded-md hover:bg-zinc-50 cursor-pointer transition-colors"
    >
      <span className={`absolute left-0 top-1 bottom-1 w-[3px] rounded-full ${barColor}`} aria-hidden />
      <div className="flex items-start gap-2">
        <span className="text-[12px] font-bold tabular-nums text-foreground flex-shrink-0 leading-tight pt-0.5">
          {fmtTime(event.starts_at)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold text-foreground truncate" title={event.title}>
            {event.title}
            {event.source === "ms_bookings" && (
              <span
                className="ml-1.5 inline-block align-middle text-[8.5px] font-bold uppercase tracking-wide bg-blue-100 text-blue-700 px-1 py-px rounded"
                title="Cita auto-importada desde Microsoft Bookings"
                data-testid={`ms-bookings-badge-${event.id}`}
              >MS</span>
            )}
          </div>
          <div className="text-[10.5px] text-muted mt-0.5 flex items-center gap-1.5 flex-wrap">
            {numSiniestro && (
              <span className="font-mono">{numSiniestro}</span>
            )}
            {peri && (
              <>
                {numSiniestro && <span>·</span>}
                <span
                  className="w-3.5 h-3.5 rounded-full bg-slate-700 text-white text-[7.5px] font-bold inline-flex items-center justify-center flex-shrink-0"
                  title={peri.name}
                >
                  {initialsOf(peri.name)}
                </span>
                <span className="truncate">{shortPeritoName(peri.name)}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function shortPeritoName(name = "") {
  // "Manuel Holgado Sánchez" → "M. Holgado"
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return `${parts[0][0]}. ${parts[1]}`;
}

function MonthGrid({ cursor, events, peritos, canSeeAll, onSelect }) {
  const start = startOfMonth(cursor);
  const gridStart = startOfWeek(start);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart); d.setDate(d.getDate() + i);
    days.push(d);
  }
  const eventsByDay = useMemo(() => {
    const m = new Map();
    events.forEach((e) => {
      const k = (e.starts_at || "").slice(0, 10);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    });
    return m;
  }, [events]);

  return (
    <div className="bg-white border border-border rounded-[10px] overflow-hidden" data-testid="cal-month-grid">
      <div className="grid grid-cols-7 text-[11px] font-semibold text-muted border-b border-border bg-[#FBFBFC]">
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
          <div key={d} className="px-3 py-2 border-r last:border-r-0 border-border">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 grid-rows-6 divide-x divide-y divide-border">
        {days.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = fmtDate(d) === fmtDate(new Date());
          const dayEvents = eventsByDay.get(fmtDate(d)) || [];
          let cellBg;
          if (isToday) cellBg = "bg-brand/5";
          else if (inMonth) cellBg = "bg-white hover:bg-background";
          else cellBg = "bg-[#FBFBFC]";
          return (
            <div
              key={fmtDate(d)}
              className={`p-1.5 min-h-[62px] transition-colors ${cellBg}`}
              data-testid={isToday ? "cal-today-cell" : undefined}
            >
              <div className={
                isToday
                  ? "bg-brand text-white rounded-full w-6 h-6 flex items-center justify-center text-[12px] font-bold mb-1"
                  : `text-[12px] font-medium mb-1 px-1 ${inMonth ? "text-foreground" : "text-[#C5C9CF]"}`
              }>{d.getDate()}</div>
              <div className="space-y-0.5">
                {dayEvents.slice(0, 2).map((ev) => <EventChip key={ev.id} event={ev} peritos={peritos} showPerito={canSeeAll} onClick={() => onSelect(ev)} />)}
                {dayEvents.length > 2 && <div className="text-[11px] font-semibold text-muted px-1 hover:text-foreground cursor-default">+{dayEvents.length - 2} más</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekGrid({ cursor, events, peritos, canSeeAll, onSelect }) {
  const start = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  const HOURS = Array.from({ length: 12 }, (_, i) => i + 8); // 8h-19h
  const eventsByDay = useMemo(() => {
    const m = new Map();
    events.forEach((e) => {
      const k = (e.starts_at || "").slice(0, 10);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    });
    return m;
  }, [events]);
  const positionEvent = (ev) => {
    const s = new Date(ev.starts_at); const e = new Date(ev.ends_at || s);
    const startH = s.getHours() + s.getMinutes() / 60;
    const endH = e.getHours() + e.getMinutes() / 60;
    const top = Math.max(0, (startH - 8) * 40);
    const height = Math.max(20, (endH - startH) * 40);
    return { top, height };
  };
  return (
    <div className="bg-white border border-border rounded-md overflow-x-auto" data-testid="cal-week-grid">
      <div className="grid" style={{ gridTemplateColumns: "60px repeat(7, minmax(120px, 1fr))" }}>
        <div className="border-b border-border bg-slate-50"></div>
        {days.map((d, i) => (
          <div key={`hdr-${fmtDate(d)}`} className="border-b border-l border-border px-2 py-2 text-xs">
            <div className="font-semibold capitalize">{d.toLocaleDateString("es-ES", { weekday: "short" })}</div>
            <div className="text-muted">{d.getDate()}</div>
          </div>
        ))}
        <div>
          {HOURS.map((h) => <div key={h} className="text-[10px] text-muted text-right pr-1 border-b border-border" style={{ height: 40 }}>{h}:00</div>)}
        </div>
        {days.map((d, di) => {
          const list = eventsByDay.get(fmtDate(d)) || [];
          return (
            <div key={`col-${fmtDate(d)}`} className="border-l border-border relative" style={{ height: HOURS.length * 40 }}>
              {HOURS.map((h) => <div key={`hl-${h}`} className="border-b border-border/40" style={{ height: 40 }}></div>)}
              {list.map((ev) => {
                const { top, height } = positionEvent(ev);
                return (
                  <div key={ev.id} onClick={() => onSelect(ev)} className="absolute left-1 right-1 cursor-pointer" style={{ top, height }}>
                    <EventChip event={ev} peritos={peritos} showPerito={canSeeAll} compact={false} fullHeight />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayList({ cursor, events, peritos, canSeeAll, onSelect }) {
  const today = fmtDate(cursor);
  const list = events.filter((e) => (e.starts_at || "").slice(0, 10) === today)
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  if (list.length === 0) return <div className="bg-white border border-border rounded-md p-8 text-center text-sm text-muted">Sin eventos para este día.</div>;
  return (
    <div className="space-y-2" data-testid="cal-day-list">
      {list.map((ev) => {
        const meta = TIPO_META[ev.tipo] || TIPO_META.otro;
        const peri = peritos.find((p) => p.id === ev.perito_id);
        const tel = ev.asegurado_movil || ev.asegurado_telefono;
        const mapsUrl = ev.location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.location)}` : null;
        const stop = (e) => e.stopPropagation();
        return (
          <div key={ev.id} onClick={() => onSelect(ev)} className={`bg-white border-l-4 border-${meta.color}-500 border border-border rounded-md px-4 py-3 cursor-pointer hover:bg-amber-50/30 flex gap-4`} data-testid={`day-event-${ev.id}`}>
            <div className="flex flex-col items-center w-16 text-xs">
              <div className="font-bold">{fmtTime(ev.starts_at)}</div>
              <div className="text-muted">{fmtTime(ev.ends_at)}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <meta.Icon className={`w-4 h-4 text-${meta.color}-600`} weight="fill" />
                <div className="font-semibold text-sm truncate">{ev.title}</div>
                {ev.source === "ms_bookings" && (
                  <span className="text-[9px] font-bold uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" title="Microsoft Bookings">MS Bookings</span>
                )}
                {canSeeAll && peri && <PeritoBadge name={peri.name} />}
                {ev.conflicts?.length > 0 && <ConflictBadges items={ev.conflicts} />}
              </div>
              {ev.location && <div className="text-xs text-muted truncate mt-0.5">{ev.location}</div>}
              {(tel || mapsUrl || ev.expediente_id) && (
                <div className="flex gap-2 mt-2 flex-wrap" onClick={stop}>
                  {tel && (
                    <a
                      href={`tel:${tel.replace(/\s+/g, "")}`}
                      onClick={stop}
                      data-testid={`day-event-call-${ev.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 active:scale-95 transition-all"
                    >
                      <Phone className="w-3 h-3" weight="fill" /> Llamar
                    </a>
                  )}
                  {mapsUrl && (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={stop}
                      data-testid={`day-event-maps-${ev.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-sky-50 text-sky-800 border border-sky-200 hover:bg-sky-100 active:scale-95 transition-all"
                    >
                      <MapPin className="w-3 h-3" weight="fill" /> Maps
                    </a>
                  )}
                  {ev.expediente_id && (
                    <a
                      href={`/review/${ev.expediente_id}`}
                      onClick={stop}
                      data-testid={`day-event-exp-${ev.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 active:scale-95 transition-all"
                    >
                      <ArrowSquareOut className="w-3 h-3" weight="fill" /> Expediente
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ===== Componentes auxiliares =====
function initialsOf(name = "") {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function PeritoBadge({ name }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-slate-100 border border-slate-200 rounded-full px-1.5 py-0.5 text-slate-700">
      <span className="w-3.5 h-3.5 rounded-full bg-amber-600 text-white flex items-center justify-center text-[8px] font-bold">{initialsOf(name)}</span>
      <span className="truncate max-w-[120px]">{name}</span>
    </span>
  );
}

function PeritosFilter({ peritos, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const label = selected.length === 0 ? "Todos los peritos" : `${selected.length} perito${selected.length > 1 ? "s" : ""}`;
  return (
    <div className="relative ml-2" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="cal-filter-peritos-btn"
        className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${selected.length > 0 ? "bg-amber-100 border-amber-300 text-amber-900" : "bg-white border-border hover:border-foreground"}`}
      >
        <UsersThree className="w-3.5 h-3.5" weight="fill" />
        <span>{label}</span>
        <CaretDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} weight="bold" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 w-64 bg-white border border-border rounded-md shadow-xl py-1 max-h-72 overflow-auto" data-testid="cal-filter-peritos-panel">
          <button
            onClick={() => onChange([])}
            className="w-full text-left text-xs px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 border-b border-border/60"
          >
            <span className="w-3.5 h-3.5 inline-flex items-center justify-center">
              {selected.length === 0 && <Check className="w-3 h-3 text-amber-700" weight="bold" />}
            </span>
            <span className="font-semibold">Todos los peritos</span>
          </button>
          {peritos.map((p) => {
            const checked = selected.includes(p.id);
            return (
              <button
                key={p.id}
                onClick={() => toggle(p.id)}
                data-testid={`cal-filter-perito-${p.id}`}
                className="w-full text-left text-xs px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2"
              >
                <span className={`w-3.5 h-3.5 inline-flex items-center justify-center border rounded ${checked ? "bg-amber-600 border-amber-600" : "border-slate-300"}`}>
                  {checked && <Check className="w-2.5 h-2.5 text-white" weight="bold" />}
                </span>
                <span className="w-5 h-5 rounded-full bg-slate-700 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">{initialsOf(p.name)}</span>
                <span className="truncate">{p.name}</span>
                {p.role && <span className="ml-auto text-[9px] uppercase tracking-wide text-muted">{p.role.replace("_", " ")}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EventChip({ event, peritos = [], showPerito = false, onClick, fullHeight }) {
  const meta = TIPO_META[event.tipo] || TIPO_META.otro;
  const peri = showPerito ? peritos.find((p) => p.id === event.perito_id) : null;
  // Paleta por tipo: barra lateral sólida, fondo muy suave, color de texto
  // y de avatar a juego con el tipo.
  const palette = {
    blue:  { bar: "bg-[#2563EB]", bg: "bg-[#EEF4FD] hover:bg-[#E1ECFB]", text: "text-[#1E40AF]", avatar: "bg-[#2563EB]" },
    teal:  { bar: "bg-[#0F766E]", bg: "bg-[#ECF6F4] hover:bg-[#DEEEEA]", text: "text-[#115E59]", avatar: "bg-[#0F766E]" },
    amber: { bar: "bg-[#D97706]", bg: "bg-[#FDF6E7] hover:bg-[#FBECCC]", text: "text-[#92400E]", avatar: "bg-[#D97706]" },
    slate: { bar: "bg-[#94A3B8]", bg: "bg-[#F4F5F7] hover:bg-[#E9EBEF]", text: "text-[#475569]", avatar: "bg-[#64748B]" },
  };
  const p = palette[meta.color] || palette.slate;
  return (
    <div
      onClick={onClick}
      className={`relative text-[11px] pl-2 pr-1 py-0.5 rounded-[4px] overflow-hidden cursor-pointer transition-colors ${p.bg} ${fullHeight ? "h-full" : ""}`}
      data-testid={`cal-event-${event.id}`}
      title={peri?.name ? `${peri.name} · ${event.title}` : event.title}
    >
      <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${p.bar}`} aria-hidden />
      {event.source === "ms_bookings" && (
        <span
          className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-blue-500 ring-1 ring-white"
          title="Microsoft Bookings"
          data-testid={`ms-bookings-dot-${event.id}`}
          aria-hidden
        />
      )}
      <div className="flex items-center gap-1.5 min-w-0">
        <span className={`font-bold tabular-nums flex-shrink-0 ${p.text}`}>{fmtTime(event.starts_at)}</span>
        <span className={`truncate flex-1 font-medium ${p.text}`}>{event.title}</span>
        {event.conflicts?.length > 0 && <Warning className="w-2.5 h-2.5 text-rose-600 flex-shrink-0" weight="fill" />}
        {peri && (
          <span
            className={`w-4 h-4 rounded-full text-white text-[8.5px] font-bold flex items-center justify-center flex-shrink-0 ${p.avatar}`}
            title={peri.name}
          >
            {initialsOf(peri.name)}
          </span>
        )}
      </div>
    </div>
  );
}

function ConflictBadges({ items }) {
  const labels = { vacation: "Vacaciones", lunch: "Comida", double_book: "Solapado" };
  const colors = { vacation: "rose", lunch: "amber", double_book: "yellow" };
  return (
    <div className="flex gap-1 flex-wrap">
      {items.map((c) => (
        <span key={c} className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-${colors[c]}-100 text-${colors[c]}-900 border border-${colors[c]}-300`}>
          <Warning className="w-2.5 h-2.5" weight="fill" /> {labels[c]}
        </span>
      ))}
    </div>
  );
}

function EventModal({ event, onClose, onOpenQuickView }) {
  const meta = TIPO_META[event.tipo] || TIPO_META.otro;
  const tel = event.asegurado_movil || event.asegurado_telefono;
  const mapsUrl = event.location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}` : null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="cal-event-modal">
        <div className="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <meta.Icon className={`w-5 h-5 text-${meta.color}-600`} weight="duotone" />
            <h2 className="font-display font-bold">{meta.label}</h2>
          </div>
          <button onClick={onClose} data-testid="cal-event-close"><X className="w-4 h-4" weight="bold" /></button>
        </div>
        <div className="px-5 py-4 space-y-2.5 text-sm">
          <div className="font-semibold">{event.title}</div>
          {event.source === "ms_bookings" && (
            <div
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-md"
              title="Cita auto-importada desde Microsoft Bookings"
              data-testid="ms-bookings-modal-badge"
            >
              <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor"><rect x="2" y="2" width="9" height="9"/><rect x="13" y="2" width="9" height="9"/><rect x="2" y="13" width="9" height="9"/><rect x="13" y="13" width="9" height="9"/></svg>
              Microsoft Bookings
            </div>
          )}
          <div className="text-xs text-muted inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" weight="bold" />
            {fmtTime(event.starts_at)}{event.ends_at ? ` – ${fmtTime(event.ends_at)}` : ""}
          </div>
          {event.location && (
            <div className="text-xs text-muted inline-flex items-start gap-1.5">
              <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" weight="fill" />
              <span>{event.location}</span>
            </div>
          )}
          {event.conflicts?.length > 0 && <ConflictBadges items={event.conflicts} />}
        </div>
        <div className="px-4 py-3 border-t border-border bg-surface/30 grid grid-cols-1 gap-2">
          {event.expediente_id && (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => onOpenQuickView?.(event.expediente_id)}
                data-testid="cal-event-quickview"
                className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 rounded-lg border bg-orange-50 text-orange-800 border-orange-200 hover:bg-orange-100 active:scale-95"
                title="Ver resumen del expediente sin salir"
              >
                <Eye className="w-4 h-4" weight="fill" /> Ver expediente
              </button>
              <a
                href={`/review/${event.expediente_id}`}
                data-testid="cal-event-go-expediente"
                className="btn-brand inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-lg"
              >
                <ArrowSquareOut className="w-4 h-4" weight="bold" /> Abrir
              </a>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <a
              href={mapsUrl || "#"}
              onClick={(e) => { if (!mapsUrl) e.preventDefault(); }}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cal-event-go-maps"
              aria-disabled={!mapsUrl}
              className={`inline-flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 rounded-lg border ${mapsUrl ? "bg-sky-50 text-sky-800 border-sky-200 hover:bg-sky-100 active:scale-95" : "bg-slate-50 text-muted border-slate-200 cursor-not-allowed opacity-60"}`}
              title={mapsUrl ? "Abrir en Google Maps" : "Sin dirección"}
            >
              <MapPin className="w-4 h-4" weight="fill" /> Cómo llegar
            </a>
            <a
              href={tel ? `tel:${String(tel).replace(/\s+/g, "")}` : "#"}
              onClick={(e) => { if (!tel) e.preventDefault(); }}
              data-testid="cal-event-go-call"
              aria-disabled={!tel}
              className={`inline-flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 rounded-lg border ${tel ? "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100 active:scale-95" : "bg-slate-50 text-muted border-slate-200 cursor-not-allowed opacity-60"}`}
              title={tel ? `Llamar al ${tel}` : "Sin teléfono"}
            >
              <Phone className="w-4 h-4" weight="fill" /> Llamar
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateEventModal({ peritos, defaultPeritoId, canSeeAll, onClose, onCreated }) {
  const [tipo, setTipo] = useState("juicio");
  // Para canSeeAll soportamos multi-selección. Si canSeeAll=false, sólo el propio perito.
  const [selectedPeritos, setSelectedPeritos] = useState(defaultPeritoId ? [defaultPeritoId] : []);
  const [allPeritos, setAllPeritos] = useState(false);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(fmtDate(new Date()));
  const [time, setTime] = useState("10:00");
  const [location, setLocation] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);

  // Tipos que envían invitación por defecto: juicio y reuniones (otro).
  // Para visita/videoperitaje, dejamos al usuario decidir.
  const inviteDefault = tipo === "juicio" || tipo === "otro";
  useEffect(() => { setSendInvite(inviteDefault); }, [inviteDefault]);

  const togglePerito = (id) => {
    setAllPeritos(false);
    setSelectedPeritos((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  };
  const toggleAll = () => {
    if (allPeritos) { setAllPeritos(false); setSelectedPeritos([]); }
    else { setAllPeritos(true); setSelectedPeritos(peritos.map((p) => p.id)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Título obligatorio"); return; }
    const ids = canSeeAll ? selectedPeritos : [defaultPeritoId];
    if (canSeeAll && ids.length === 0) { toast.error("Selecciona al menos un perito"); return; }
    setBusy(true);
    try {
      const startsAt = `${date}T${time}:00`;
      if (canSeeAll && (allPeritos || ids.length > 1)) {
        const r = await api.post("/calendar/events/bulk", {
          perito_ids: allPeritos ? [] : ids,
          all_peritos: allPeritos,
          tipo, title, location: location || undefined,
          starts_at: startsAt,
          send_invites: sendInvite,
        });
        const sent = r.data?.invites?.sent ?? 0;
        const skipped = r.data?.invites?.skipped ?? 0;
        let msg = `Evento creado para ${r.data.count} perito${r.data.count === 1 ? "" : "s"}`;
        if (sendInvite && sent > 0) msg += ` · 📧 ${sent} invitación${sent === 1 ? "" : "es"} enviada${sent === 1 ? "" : "s"}`;
        else if (sendInvite && skipped > 0) msg += " · ⚠️ Sin SMTP, invitaciones no enviadas";
        toast.success(msg);
      } else {
        await api.post(`/calendar/events?send_invite=${sendInvite}`, {
          perito_id: ids[0], tipo, title, location: location || undefined,
          starts_at: startsAt,
        });
        toast.success(sendInvite ? "Evento creado y invitación enviada" : "Evento creado");
      }
      onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="cal-create-modal">
        <div className="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold">Nuevo evento</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" weight="bold" /></button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <label className="block">
            <span className="text-[11px] font-semibold text-muted block mb-1">Tipo</span>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="w-full text-xs border border-border rounded-md px-2 py-1.5" data-testid="cal-create-tipo">
              {Object.entries(TIPO_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
          </label>
          {canSeeAll && peritos.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold text-muted">
                  Peritos {selectedPeritos.length > 0 && <span className="text-amber-700">({selectedPeritos.length} seleccionados)</span>}
                </span>
                <button
                  type="button"
                  onClick={toggleAll}
                  data-testid="cal-create-all-peritos"
                  className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full border transition-colors ${allPeritos ? "bg-amber-600 text-white border-amber-600" : "border-border hover:border-foreground"}`}
                >
                  <UsersThree className="w-3 h-3 inline mr-1" weight="fill" />
                  Todo el equipo
                </button>
              </div>
              <div className="max-h-40 overflow-auto border border-border rounded-md bg-white divide-y divide-border/60" data-testid="cal-create-peritos-list">
                {peritos.map((p) => {
                  const checked = selectedPeritos.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePerito(p.id)}
                      data-testid={`cal-create-perito-${p.id}`}
                      className="w-full text-left text-xs px-2.5 py-1.5 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <span className={`w-3.5 h-3.5 inline-flex items-center justify-center border rounded ${checked ? "bg-amber-600 border-amber-600" : "border-slate-300"}`}>
                        {checked && <Check className="w-2.5 h-2.5 text-white" weight="bold" />}
                      </span>
                      <span className="w-5 h-5 rounded-full bg-slate-700 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">{initialsOf(p.name)}</span>
                      <span className="truncate flex-1">{p.name}</span>
                      {p.role && <span className="text-[9px] uppercase tracking-wide text-muted">{p.role.replace("_", " ")}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <label className="block">
            <span className="text-[11px] font-semibold text-muted block mb-1">Título *</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="cal-create-title" className="w-full text-xs border border-border rounded-md px-2 py-1.5" placeholder="Reunión de equipo / Citación juicio Madrid" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] font-semibold text-muted block mb-1">Fecha</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required className="w-full text-xs border border-border rounded-md px-2 py-1.5" />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-muted block mb-1">Hora</span>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} required className="w-full text-xs border border-border rounded-md px-2 py-1.5" />
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] font-semibold text-muted block mb-1">Ubicación</span>
            <input value={location} onChange={(e) => setLocation(e.target.value)} className="w-full text-xs border border-border rounded-md px-2 py-1.5" placeholder="Sala de juntas · Juzgado nº 5 Madrid" />
          </label>
          <label className="flex items-start gap-2 text-xs cursor-pointer pt-1" data-testid="cal-create-send-invite">
            <input
              type="checkbox"
              checked={sendInvite}
              onChange={(e) => setSendInvite(e.target.checked)}
              className="mt-0.5"
            />
            <span className="flex-1">
              <span className="font-semibold inline-flex items-center gap-1">
                <EnvelopeSimple className="w-3.5 h-3.5" weight="fill" /> Enviar invitación por email
              </span>
              <span className="block text-[10px] text-muted mt-0.5">
                Cada perito recibirá el evento con un archivo <code>.ics</code> adjunto para añadirlo a Outlook / Google Calendar.
              </span>
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-surface/30">
          <button type="button" onClick={onClose} className="text-xs px-3 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button type="submit" disabled={busy} data-testid="cal-create-submit" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Plus className="w-3 h-3" weight="bold" /> Crear
          </button>
        </div>
      </form>
    </div>
  );
}
